/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {getScreenWidth, IndexedObject, remToPx} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import {flatten} from 'lodash';

import VatDeclarationItem from '../models/vatDeclarationItemModel';

import {BolInvoice, CategoryType, VATPeriod, TaxTotalsData, VatDeclarationInvalidation, VatPeriod} from './interfaces';

import {getMenuWidth} from './menuUtil';

import {getCurrentMonth, getCurrentQuarter, getCurrentYear, getQuarterLastMonth} from './utils';

export function calculateTaxes(
    revenueItems: VatDeclarationItem[],
    costItems: VatDeclarationItem[]
): IndexedObject<TaxTotalsData> {
    const taxesPerCountry: IndexedObject<TaxTotalsData> = {};
    for (const item of revenueItems.concat(costItems)) {
        const {categoryType, data} = item;
        const dataKey: keyof TaxTotalsData = categoryType === CategoryType.REVENUE ? 'tax' : 'preTax';
        const countryCodes = item.getCountryCodes();
        for (const countryCode of countryCodes) {
            if (!(countryCode in taxesPerCountry))
                taxesPerCountry[countryCode] = {
                    tax: {},
                    preTax: {},
                    revenueIncl: {},
                    revenueExcl: {},
                    reversedIncl: {}
                };
            for (const [vatPercentage, vatValues] of Object.entries(data[countryCode])) {
                const taxTotals = taxesPerCountry[countryCode];
                if (!Object.hasOwn(taxTotals[dataKey], vatPercentage))
                    taxTotals[dataKey][vatPercentage] = vatValues.total_vat;
                else taxTotals[dataKey][vatPercentage] += vatValues.total_vat;
            }
        }
    }
    return taxesPerCountry;
}

export function calculateRevenues(revenueItems: VatDeclarationItem[]): IndexedObject<TaxTotalsData> {
    const revPerCountry: IndexedObject<TaxTotalsData> = {};
    for (const item of revenueItems) {
        const {data} = item;
        const countryCodes = item.getCountryCodes();
        for (const countryCode of countryCodes) {
            if (!(countryCode in revPerCountry))
                revPerCountry[countryCode] = {tax: {}, preTax: {}, revenueIncl: {}, revenueExcl: {}, reversedIncl: {}};
            for (const [vatPercentage, vatValues] of Object.entries(data[countryCode])) {
                const taxTotals = revPerCountry[countryCode];
                if (!Object.hasOwn(taxTotals.revenueExcl, vatPercentage))
                    taxTotals.revenueExcl[vatPercentage] = vatValues.total_incl_vat - vatValues.total_vat;
                else taxTotals.revenueExcl[vatPercentage] += vatValues.total_incl_vat - vatValues.total_vat;

                if (!Object.hasOwn(taxTotals.revenueIncl, vatPercentage))
                    taxTotals.revenueIncl[vatPercentage] = vatValues.total_incl_vat;
                else taxTotals.revenueIncl[vatPercentage] += vatValues.total_incl_vat;

                if (!Object.hasOwn(taxTotals.reversedIncl, vatPercentage))
                    taxTotals.reversedIncl[vatPercentage] = vatValues.total_incl_reversed_vat;
                else taxTotals.revenueIncl[vatPercentage] += vatValues.total_incl_reversed_vat;
            }
        }
    }
    return revPerCountry;
}

export function getBolInvoices(indexed: IndexedObject<VatDeclarationItem[]>): BolInvoice[] {
    const indexedInvoices: IndexedObject<BolInvoice> = {};
    for (const items of Object.values(indexed)) {
        for (const item of items) {
            for (const invoice of item.bolInvoices) {
                if (invoice.invoice_id in indexedInvoices) continue;
                indexedInvoices[invoice.invoice_id] = invoice;
            }
        }
    }
    const invoices = Object.values(indexedInvoices);
    invoices.sort((invA: BolInvoice, invB: BolInvoice) => invA.period_start.localeCompare(invB.period_start));
    return invoices;
}

export function groupVatItems(
    itemsMap: IndexedObject<VatDeclarationItem[]>,
    sort: boolean = true
): VatDeclarationItem[] {
    const groupedItems: IndexedObject<VatDeclarationItem> = {};
    const items = flatten(Object.values(itemsMap));
    for (const item of items) {
        const {category} = item;
        if (!Object.hasOwn(groupedItems, category)) {
            groupedItems[category] = item.clone();
            continue;
        }
        groupedItems[category].merge(item.clone());
    }
    const declItems = Object.values(groupedItems);
    if (sort)
        declItems.sort((itemA: VatDeclarationItem, itemB: VatDeclarationItem) => {
            const catA = itemA.categoryType,
                catB = itemB.categoryType;
            if (catA === catB) return 0;
            if (catA === CategoryType.REVENUE) return catB === CategoryType.COSTS ? -1 : 0;
            return catB === CategoryType.REVENUE ? 1 : 0;
        });
    return Object.values(declItems);
}

export function vatDeclarationIsInvalidated(
    items: IndexedObject<VatDeclarationItem[]>
): VatDeclarationInvalidation | null {
    for (const item of flatten(Object.values(items))) if (item.invalidation !== null) return item.invalidation;
    return null;
}

export function getVatPeriods(periodType: VATPeriod): VatPeriod[] {
    const periods: VatPeriod[] = [];
    const current = getCurrentPeriod(periodType);
    const last = getPreviousVatPeriod(periodType, current.start);
    periods.push({...last, label: getVatPeriodLabel(periodType, last.start)});
    let start: IDate = last.start;
    for (let idx = 0; idx < getExpectedPeriodCount() - 1; idx++) {
        const prevPeriod = getPreviousVatPeriod(periodType, start);
        start = prevPeriod.start;
        periods.push({...prevPeriod, label: getVatPeriodLabel(periodType, start)});
    }
    return periods.reverse();
}

function getCurrentPeriod(periodType: VATPeriod): {start: IDate; end: IDate} {
    if (periodType === VATPeriod.MONTH) return getCurrentMonth();
    else if (periodType === VATPeriod.QUARTER) return getCurrentQuarter();
    else return getCurrentYear();
}

export function getVatPeriodLabel(periodType: VATPeriod, start: IDate): string {
    if (periodType === VATPeriod.MONTH) {
        return start.format('MMMM YYYY');
    } else if (periodType === VATPeriod.QUARTER) return `Q${start.format('Q YYYY')}`;
    else return start.format('YYYY');
}

function getExpectedPeriodCount(): number {
    const itemWidth = remToPx(5.2);
    const padding = remToPx(1);
    const screenWidth = getScreenWidth();
    const menuWidth = getMenuWidth();
    return Math.floor((screenWidth - menuWidth - padding * 2) / itemWidth) - 4; // Subtract 2 for nav buttons
}

export function getPreviousVatPeriod(periodType: VATPeriod, start: IDate): {start: IDate; end: IDate} {
    start = start.clone();
    let end: IDate;
    if (periodType === VATPeriod.MONTH) {
        start = start.subtract(1, 'month');
        end = start.clone().day(start.daysInMonth());
    } else if (periodType === VATPeriod.QUARTER) {
        start = start.subtract(1, 'quarter');
        end = start.clone().month(getQuarterLastMonth(start.quarter()));
        end.day(end.daysInMonth());
    } else {
        start = start.subtract(1, 'year');
        end = start.clone().set({month: 12, day: 31});
    }
    return {start, end};
}

export function getNextVatPeriod(periodType: VATPeriod, start: IDate): {start: IDate; end: IDate} {
    start = start.clone();
    let end: IDate;
    if (periodType === VATPeriod.MONTH) {
        start = start.add(1, 'month');
        end = start.clone().day(start.daysInMonth());
    } else if (periodType === VATPeriod.QUARTER) {
        start = start.add(1, 'quarter');
        end = start.clone().month(getQuarterLastMonth(start.quarter()));
        end.day(end.daysInMonth());
    } else {
        start = start.add(1, 'year');
        end = start.clone().set({month: 12, day: 31});
    }
    return {start, end};
}
